import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG } from "@/i18n";
import { useLanguages } from "@/hooks/useLanguages";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface SkillFramework {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
}

interface SkillFrameworkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  framework: SkillFramework | null;
  onSuccess: () => void;
}

export const SkillFrameworkDialog = ({
  isOpen,
  onClose,
  framework,
  onSuccess,
}: SkillFrameworkDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_default: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { i18n } = useTranslation();
  const { data: languages } = useLanguages();
  const [activeLang, setActiveLang] = useState<string>(i18n.language || DEFAULT_LANG);
  const [nameI18n, setNameI18n] = useState<Record<string, string>>({});
  const [descI18n, setDescI18n] = useState<Record<string, string>>({});

  useEffect(() => {
    if (framework) {
      setFormData({
        name: framework.name || "",
        description: framework.description || "",
        is_default: framework.is_default || false,
      });
      const f: any = framework as any;
      setNameI18n((f.name_i18n as any) || {});
      setDescI18n((f.description_i18n as any) || {});
    } else {
      setFormData({
        name: "",
        description: "",
        is_default: false,
      });
      setNameI18n({});
      setDescI18n({});
    }
  }, [framework]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Framework name is required");
      return;
    }

    setIsSubmitting(true);

  try {
      const submitData: any = {
        ...formData,
        name_i18n: nameI18n,
        description_i18n: descI18n,
      };
      if (framework) {
        // Update existing framework
        const { error } = await supabase
          .from('skill_frameworks')
          .update(submitData)
          .eq('id', framework.id);

        if (error) throw error;
        toast.success("Skill framework updated successfully");
      } else {
        // Create new framework
        const { error } = await supabase
          .from('skill_frameworks')
          .insert([submitData]);

        if (error) throw error;
        toast.success("Skill framework created successfully");
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving skill framework:', error);
      toast.error("Failed to save skill framework");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {framework ? "Edit Skill Framework" : "Create New Skill Framework"}
          </DialogTitle>
          <DialogDescription>
            {framework
              ? "Update the skill framework details below."
              : "Create a new skill framework to organize competencies and skills."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Framework Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Leadership Competencies, Technical Skills"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the purpose and scope of this skill framework..."
              rows={3}
            />
          </div>

          <div className="space-y-2 border rounded-md p-3">
            <div className="flex items-center justify-between">
              <Label>Translations</Label>
              <div className="w-48">
                <Select value={activeLang} onValueChange={(v) => setActiveLang(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Language" />
                  </SelectTrigger>
                  <SelectContent>
                    {(languages || []).map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.native_name} ({l.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Localized Name ({activeLang})</Label>
                <Input
                  value={nameI18n[activeLang] || ''}
                  onChange={(e) => setNameI18n({ ...nameI18n, [activeLang]: e.target.value })}
                  placeholder="Translated framework name"
                />
              </div>
              <div className="space-y-2">
                <Label>Localized Description ({activeLang})</Label>
                <Textarea
                  value={descI18n[activeLang] || ''}
                  onChange={(e) => setDescI18n({ ...descI18n, [activeLang]: e.target.value })}
                  placeholder="Translated description"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_default"
              checked={formData.is_default}
              onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked as boolean })}
            />
            <Label htmlFor="is_default">Set as default framework</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : framework ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};